'use strict'
import React, { useCallback, useEffect, useReducer, useState } from 'react'
import { produce } from 'immer'
import isEqual from 'lodash.isequal'
import _set from 'lodash/set'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Link as RouterLink } from 'react-router-dom'
import { Link } from '@mui/material'
import MenuIcon from '@mui/icons-material/Menu'
import { SpinnerButton, DeleteButton } from '../../common/CustomButton'
import { DndContext, closestCenter } from '@dnd-kit/core'
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import 'react-datepicker/dist/react-datepicker.css'
import { errorLog } from '../../../common/logger/Logger'
import { viewId, titleName, masterClassification } from '../../../_constants/Code'
import { useModifyClassList as modifyClassList } from '../../../common/utils/CSSUtils'
import { getMessage } from '../../../common/utils/MessageUtils'

import CustomTooltip from '../../../_components/CustomTooltip'
import Forbidden from '../../../_components/Forbidden'
import { useModal, useModalExtra } from '../../../_components/Modal'
// 型定義
import './extra/types'
import { setCategoryInfoInitialData, getInitialDataByCategoryName, handleBeforeUnload } from './extra/common'
import { fetchMaster, fetchCategory, fetchCategoryTag, updateCategory } from './extra/services'
import CategoryNameModalContent from './components/CategoryNameModalContent'
import { ShowErrors, ShowServerErrors } from './components/ShowErrors'

/*==================================
  状態管理・Reducer
===================================*/
/**
 * Reducer関数
 *
 * @param {CategoryState} state
 * @param {Action} action
 */
const categoryReducer = (state, action) => {
  return produce(state, (draft) => {
    switch (action.type) {
      case 'INIT_STATE': {
        const apiData = action.payload
        draft.splice(0, draft.length)
        apiData.forEach((item) => {
          draft.push({
            ...item,
            uiState: {
              deleted: false,
              hasChanges: false,
              previousHasChanges: false,
              registered: true,
              hasError: false,
              initialCategoryName: item.categoryName
            }
          })
        })
        break
      }
      case 'MOVE_ITEM': {
        const { oldIndex, newIndex } = action.payload
        const newDraft = arrayMove(draft, oldIndex, newIndex)
        // 新しい順位に置き換え
        draft.splice(0, draft.length, ...newDraft)
        break
      }
      case 'UPDATE_ITEM': {
        const updateItem = action.payload
        const item = draft.find((item) => item.id === updateItem.id)
        if (item) {
          // 更新内容比較
          const initialItem = getInitialDataByCategoryName(item.categoryName) || {}
          const initialData = {
            categoryName: initialItem.categoryName
          }
          const updateData = {
            categoryName: updateItem.categoryName
          }

          const hasChanges = !isEqual(initialData, updateData)
          item.uiState.hasChanges = hasChanges

          const { uiState, ...rest } = updateItem
          Object.assign(item, rest)
        }
        break
      }
      case 'DELETE_ITEM': {
        const id = action.payload
        const index = draft.findIndex((item) => item.id === id)
        if (index !== -1) {
          if (draft[index].uiState.registered) {
            // 削除前の変更状態を保持
            draft[index].uiState.previousHasChanges = draft[index].uiState.hasChanges
            draft[index].uiState.hasChanges = true
            draft[index].uiState.deleted = true
          } else {
            draft.splice(index, 1)
          }
        }
        break
      }
      case 'RESTORE_ITEM': {
        const id = action.payload
        const item = draft.find((item) => item.id === id)
        if (item && item.uiState.deleted) {
          // 削除前の変更状態に戻す
          item.uiState.hasChanges = item.uiState.previousHasChanges || false
          item.uiState.deleted = false
        }
        break
      }
      case 'ADD_ITEM': {
        const newItem = action.payload
        draft.push({
          ...newItem,
          uiState: {
            deleted: false,
            hasChanges: false,
            previousHasChanges: false,
            registered: false,
            hasError: false
          }
        })
        break
      }
      case 'SET_CLIENT_ERROR': {
        const errorStateIds = action.payload
        draft.forEach((item) => {
          item.uiState.hasError = errorStateIds.includes(item.id)
        })
        break
      }
      default:
        return state
    }
  })
}

/**
 * @function useRDispatch
 * @param {React.Dispatch<any>} dispatch
 * @returns {(type: string, payload: any) => void}
 */
export const useRDispatch = (dispatch) => useCallback((type, payload) => dispatch({ type, payload }), [dispatch])

/**
 * オブジェクトキー存在チェック
 *
 * @param {object} errors - チェック対象のオブジェクト
 * @returns {boolean} オブジェクトにキーが存在する場合は true
 */
export function hasErrors(errors) {
  return Object.values(errors).some((arr) => Array.isArray(arr) && arr.length > 0)
}

const CategoryInfoContent = (props) => {
  const [isLoaded, setIsLoaded] = useState(false)
  const [master, setMaster] = useState([])
  const [category, setCategory] = useState([])
  const [categoryTag, setCategoryTag] = useState([])
  const [clientError, setClientError] = useState({})
  const [serverError, setServerError] = useState({})

  /** @type {categoryState} */
  const initialState = []

  /** @type {ReturnType<typeof useReducer<typeof categoryReducer, typeof initialState>>} */
  const [categoryState, _dispatch] = useReducer(categoryReducer, initialState)
  const localDispatch = useRDispatch(_dispatch)

  // クライアントエラー判定
  const isClientError = hasErrors(clientError)

  /*==================================
    初期処理
  ===================================*/
  useEffect(() => {
    document.title = props.pageTitle
    init()
    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [])

  const init = async () => {
    // マスター情報取得
    const pMaster = fetchMaster(props.corporationNumber)
    // カテゴリー情報取得
    const pCategory = fetchCategory(props.corporationNumber)
    // カテゴリータグ情報取得
    const pCategoryTag = fetchCategoryTag(props.corporationNumber)

    const [master, category, categoryTag] = await Promise.all([pMaster, pCategory, pCategoryTag])

    setMaster(master)
    setCategory(category)
    setCategoryTag(categoryTag)

    // ソートと表示用の加工
    if (category.length) {
      const processedData = category.map((row) => {
        // const [CategoryKey, CategoryName] = row
        return {
          id: crypto.randomUUID(),
          categoryKey: row.CategoryKey,
          categoryName: row.CategoryName,
          applyNum: countApply(row.CategoryKey, categoryTag)
        }
      })
      // モジュール変数で初期データを保持
      setCategoryInfoInitialData(processedData)
      localDispatch('INIT_STATE', processedData)
    }
    setIsLoaded(true)
  }

  function countApply(categoryKey, categoryTag) {
    // カテゴリーキーがいくつのダッシュボードに適用されているかを求める
    return categoryTag.filter((item) => item.CategoryKey === categoryKey).length
  }

  // 入力エラー時にスクロールをトップへ
  useEffect(() => {
    if (hasErrors(clientError) || hasErrors(serverError)) {
      window.scrollTo(0, 0)
    }
  }, [clientError, serverError])

  /*==================================
    イベントハンドラ
  ===================================*/
  /*  ===== ドラッグ＆ドロップ ===== */
  /**
   * レコード順序変更
   *
   * @param {イベント} e
   */
  const handleDragEnd = useCallback(
    (e) => {
      const { active, over } = e
      if (!over || active.id === over.id) return

      const oldIndex = categoryState.findIndex((item) => item.id === active.id)
      const newIndex = categoryState.findIndex((item) => item.id === over.id)

      if (oldIndex !== newIndex) {
        localDispatch('MOVE_ITEM', { oldIndex, newIndex })
      }
    },
    [categoryState, localDispatch]
  )

  /*  ===== レコード削除・リストア ===== */
  /**
   * レコード削除処理（削除アクションをdispatch）
   *
   * @param {CategoryState.id} id
   */
  const handleDelete = useCallback(
    (id) => {
      localDispatch('DELETE_ITEM', id)
    },
    [localDispatch]
  )

  /**
   * レコードリストア処理（復帰アクションをdispatch）
   *
   * @param {CategoryState.id} id
   */
  const handleRestore = useCallback(
    (id) => {
      localDispatch('RESTORE_ITEM', id)
    },
    [localDispatch]
  )

  /**
   * レコード追加処理（登録アクションをdispatch）
   *
   * @param {CategoryState} item
   */
  const handleRegiste = useCallback(
    (item) => {
      localDispatch('ADD_ITEM', item)
    },
    [localDispatch]
  )

  /**
   * レコード追加処理（復帰アクションをdispatch）
   *
   * @param {CategoryState} item
   */
  const handleUpdate = useCallback(
    (item) => {
      localDispatch('UPDATE_ITEM', item)
    },
    [localDispatch]
  )

  /**
   * クライアントエラー設定処理（エラーセットアクションをdispatch）
   *
   * @param {CategoryState} errorIds
   */
  const handleSetClientError = useCallback(
    (errorIds) => {
      localDispatch('SET_CLIENT_ERROR', errorIds)
    },
    [localDispatch]
  )

  /*==================================
    入力チェック・ヘルパー関数
  ===================================*/
  /** 入力チェック */
  const _checkInput = async () => {
    const errors = {
      categoryNameTable: []
    }

    if (hasErrors(errors)) {
      // エラーメッセージ設定
      setClientError(errors)
      return true
    } else {
      // エラークリア
      handleSetClientError([])
      setClientError({})
      return false
    }
  }

  /**
   * 更新前のAPI送信用データ整形
   *
   * @param {CategoryState[]} categoryState - 現在の状態管理配列
   * @returns {Object[]} API送信用配列
   */
  function prepareTableData(categoryState) {
    return (
      categoryState
        // .filter((item) => !item.uiState.deleted)
        .map((item, index) => {
          const { id, ...rest } = item // id を除外
          return {
            category_key: item.categoryKey,
            category_name: item.categoryName,
            sort: index + 1, // 1始まりの表示順を付与
            delete_flg: item.uiState.deleted ? 1 : 0
          }
        })
    )
  }

  /**
   * 更新前のAPI送信用データチェック処理
   *
   * @returns {boolean} チェック結果
   */
  const prepareCheck = async () => {
    // サーバーエラーメッセージをクリア
    setServerError({})
    // チェック処理
    const hasError = await _checkInput()
    if (hasError) {
      return true
    }

    return false
  }

  /*==================================
    更新処理
  ===================================*/
  /** 更新処理 */
  const handleCategoryUpdate = async () => {
    // チェック処理
    const hasError = await prepareCheck()
    if (hasError) {
      return
    }

    // API送信用に加工されたファイルデータ
    const payload = prepareTableData(categoryState)
    try {
      // 用途タグ更新
      // 更新前処理
      const res = await updateCategory(
        props.corporationNumber,
        props.corporationName,
        props.userName,
        props.userId,
        payload
      )
      if (!res) throw res
      props.navigate('/master/masterList', { state: { message: 'データ反映を開始しました。' } })
    } catch (error) {
      const serverErrorMsg = getMessage('E011012')
      setServerError({ messages: [serverErrorMsg] })
      errorLog('E000001', props.userId, viewId.categoryInfo, 'updateCategory', error)
    }
  }

  /*==================================
    コンポーネント（JSX）
  ===================================*/
  /**
   * SortableItemコンポーネント
   *
   * @param {object} props
   * @param {string} props.id - アイテムの一意ID
   * @param {CategoryState} props.categoryNameInfo - アイテム情報
   */
  const SortableItem = ({ id, categoryNameInfo }) => {
    const isDisabled = categoryNameInfo.uiState.deleted
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id, disabled: isDisabled })
    const lineThroughClass = isDisabled ? '!line-through !text-gray-900 !decoration-gray-900' : ''
    const errorClasses = 'outline outline-[1px] outline-[#f4392c]'

    const hasError = categoryNameInfo.uiState.hasError

    const style = {
      transform: CSS.Transform.toString(transform),
      transition
    }

    const disabledStyle = {
      pointerEvents: isDisabled ? 'none' : 'auto',
      cursor: isDisabled ? 'default' : 'pointer'
    }

    return (
      <tr
        ref={setNodeRef}
        style={style}
        {...attributes}
        className={`${isDisabled && 'bg-gray-300'} ${hasError ? errorClasses : ''}`}
      >
        <td className="cursor-pointer order-icon" style={disabledStyle} {...listeners}>
          <MenuIcon />
        </td>
        <td className="cursor-default">
          <CustomTooltip title={categoryNameInfo.categoryName} useParentWidth={true}>
            {(ref) => (
              <Link
                className="w-full truncate !text-left"
                ref={ref}
                variant="button"
                underline="none"
                component={'button'}
                onClick={() => !isDisabled && openCategoryNameModal('update', categoryNameInfo)}
                style={disabledStyle}
                tabIndex={isDisabled ? -1 : 0}
                aria-disabled={isDisabled}
              >
                <span className={`font-Medium truncate ${lineThroughClass}`} style={{ textTransform: 'none' }}>
                  {categoryNameInfo.categoryName}
                </span>
              </Link>
            )}
          </CustomTooltip>
        </td>
        <td style={{ textAlign: 'right' }}>
          <span className="cursor-text">{categoryNameInfo.applyNum}</span>
        </td>
        <td className="cursor-default">
          <DeleteButton
            className="flex"
            onDelete={() => handleDelete(categoryNameInfo.id)}
            onRestore={() => handleRestore(categoryNameInfo.id)}
            isRestore={categoryNameInfo.uiState.deleted}
          />
        </td>
      </tr>
    )
  }
  const CategoryTable = () => {
    return (
      <>
        {categoryState.length ? (
          <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd} autoScroll={false}>
            <div className="setting-table is-segment">
              <table className="setting-table__table table-fixed w-full form-control">
                <colgroup>
                  <col className="w-[4%]" />
                  <col className="w-[54%]" />
                  <col className="w-[4%]" />
                  <col className="w-[3%]" />
                </colgroup>
                <thead>
                  <tr>
                    <th className="!text-center">↑↓</th>
                    <th>用途タグ名</th>
                    <th>適用数</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody className="text-caption-1">
                  <SortableContext items={categoryState.map((item) => item.id)} strategy={verticalListSortingStrategy}>
                    {categoryState.map((item) => {
                      return <SortableItem key={item.id} id={item.id} categoryNameInfo={item} />
                    })}
                  </SortableContext>
                </tbody>
              </table>
              {/* エラーメッセージ表示 */}
              <ShowErrors clientErrors={clientError} fieldName="categoryTable" />
            </div>
          </DndContext>
        ) : (
          <div className="text-center my-10 pt-10">
            <span className="text-h5 red--text">用途タグが未登録です。</span>
          </div>
        )}
      </>
    )
  }
  const useModalContent = () => {
    const { _closeUpModal } = props.modalExtra

    const onClose = () => {
      _closeUpModal()
    }

    /**
     * @function 用途タグ設定ダイアログの表示/
     * @param {string} mode - 操作種別（regist: 登録、update: 更新）
     * @param {CategoryState} updateData 更新データ
     */
    const openCategoryNameModal = (mode, updateData = null) => {
      const key = Math.random()
      const submitHandler = mode === 'update' ? handleUpdate : handleRegiste

      props.showModal({
        title: '用途タグ名設定',
        body: (
          <CategoryNameModalContent
            key={key}
            categoryNamesInfo={categoryState}
            updateData={updateData}
            editMode={mode}
            onClose={onClose}
            onSubmit={(data) => {
              submitHandler(data)
              _closeUpModal()
            }}
          />
        ),
        isFooter: false,
        isCancel: false,
        isWide: false
      })
    }

    return openCategoryNameModal
  }

  const openCategoryNameModal = useModalContent()

  /** @function openConfirmUpdateModal データ更新モダルを表示 */
  async function openConfirmUpdateModal() {
    const { promisify } = modifyClassList('.modal-window', ['w-[900px]'])

    // 表の中に 適用数が1以上 かつ 削除状態の行があるか判定
    const hasAppliedDeleted = categoryState.some((item) => item.applyNum >= 1 && item.uiState.deleted)

    await promisify((res) => {
      props.showModal({
        title: '用途タグ登録の確認',
        onClose: res,
        isWide: false,
        description: (
          <React.Fragment>
            <div className="text-center">設定値を登録します。よろしいですか？</div>
            {hasAppliedDeleted && (
              <div className="pt-5 text-center" style={{ color: 'red' }}>
                ※ダッシュボードへ適用中の用途タグが削除対象に設定されています。
                <br />
                一度削除すると復元できません。
              </div>
            )}
          </React.Fragment>
        ),
        button: [
          {
            name: '実行',
            className: 'footer-btn button_base',
            click: () => handleCategoryUpdate()
          }
        ]
      })
    })
  }

  /** メインコンテンツ */
  if (!isLoaded) {
    return null
  }
  return (
    <React.Fragment>
      <main className="page-container">
        <section className="page-content">
          <div className="centered-container">
            {/* 上階層に戻る */}
            <div className="bg main">
              <div className="back-upper-level py-8 pl-8 border-b border-blue2-lighten-20">
                <Link color="inherit" variant="button" underline="none" component={RouterLink} to="/master/masterList">
                  <span className="back-upper-level__link link--text hover:lighten-70 flex items-center">
                    <span className="icon-arrow-circle icon-32px mr-4 rotate-180 relative after:absolute after:bg-white after:w-full after:h-full after:rounded-full after:left-0 after:-z-10"></span>
                    <span className="text-h5 font-bold">{titleName.masterList}画面に戻る</span>
                  </span>
                </Link>
              </div>
            </div>
            {/* コンテンツヘッダ */}
            <header className="page-header flex justify-center py-1">
              <h1 className="page-header__name text-h1 grey--text py-8">
                {masterClassification[master.Classification].name}
              </h1>
            </header>
            {/* メインコンテンツ */}
            <div className="page-body">
              <ShowServerErrors serverErrors={serverError} />
              {isClientError && (
                <div>
                  <span style={{ color: 'red' }}>入力内容に誤りがあります。</span>
                </div>
              )}
              <div className="box-xy-bordered rounded-t-3xl rounded-b-2xl pt-10 pb-8 px-16 border-blue1 base-table-header ">
                <h2 className="box-bordered-heading blue1 white--text text-h3 font-medium pt-2.5 pb-3 px-14 -mt-10 -mx-16 rounded-t-2xl">
                  用途タグ設定
                </h2>
                <div className="flex items-center justify-between my-6">
                  ダッシュボード上で設定できる用途タグを管理します。
                </div>
                <div className="flex justify-between gap-x-5 mb-3 items-center btn-group !float-none">
                  <span className="text-body-2-medium grow  border-r border-blue2-lighten-20">
                    登録数：{categoryState.length}件
                  </span>

                  <Link
                    className="header-btn !p-0 "
                    color="inherit"
                    variant="button"
                    underline="none"
                    component={'button'}
                    onClick={() => openCategoryNameModal('regist')}
                  >
                    <span className="header-btn white--text py-1.5 pl-7 pr-10 inline-flex items-center">
                      <span className="icon-plus-line icon-32px mr-1.5 my-px"></span>
                      <span className="-translate-y-px text-h7">新規追加</span>
                    </span>
                  </Link>
                </div>
                {/*  ===== 用途タグ設定テーブル ===== */}
                <CategoryTable />
                <div className="content-footer mt-16 mb-8">
                  <div className="border-t border-blue1" />
                  {/*  ===== フッターボタン ===== */}
                  <div className="footer_button_area mt-8">
                    <Link
                      color="inherit"
                      variant="button"
                      underline="none"
                      component={RouterLink}
                      to="/master/masterList"
                    >
                      <button type="button" className="footer-btn button_back">
                        キャンセル
                      </button>
                    </Link>
                    <SpinnerButton
                      name="登録"
                      className="footer-btn button_base"
                      disabled={categoryState.length === 0}
                      onClick={() => openConfirmUpdateModal()}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </React.Fragment>
  )
}

const CategoryInfo = () => {
  const state = useSelector((state) => state)
  const navigate = useNavigate()
  const showModal = useModal()
  const modalExtra = useModalExtra()

  // 権限を持っていない場合は画面表示しない
  if (
    !state.authority.CategoryTag_AuthInfo.Availability ||
    !state.authority.CategoryTag_AuthInfo.Auth_Conf.CategoryTag_Set
  ) {
    return <Forbidden />
  }

  return (
    <CategoryInfoContent
      pageTitle={titleName.categoryInfo + titleName.common}
      corporationNumber={state.company.corporationNumber}
      corporationName={state.company.name}
      userId={state.user.sub}
      userName={state.user.userName}
      authority={state.authority}
      mode={state.analysis.mode}
      bucketName={'chrofy-' + process.env.REACT_APP_ENV + '-' + state.company.corporationNumber}
      navigate={navigate}
      showModal={showModal}
      modalExtra={modalExtra}
    />
  )
}

export default CategoryInfo
